<template>
  <component :is="nativeMenuComponent" :is-open="showMegaMenu" @close="showMegaMenu = false" />

  <div class="tab-bar">
    <div class="tab-bar-container">
      <button @click="openMobileMenu">
        <span class="nav-icon logo-icon">
          <IonIcon :icon="logoUrl" />
        </span>
      </button>

      <button @click="toggleUserMenuModal">
        <span class="nav-icon">
          <IonIcon :icon="personCircleOutline" color="586B7D" />
        </span>
      </button>
    </div>

    <UserMenuModal :is-open="showUserMenuModal" @close="showUserMenuModal = false" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, type Component } from 'vue';
import { IonIcon } from '@ionic/vue';
import { personCircleOutline } from 'ionicons/icons';
import { useLayout } from '@/layouts/composables/useLayout';
import { USER_TYPE } from '@/common/constants';
import { NativeMenuStudent, NativeMenuEmployee } from '@/layouts/components/menu-content';
import UserMenuModal from '@/features/common/user/user-avatar/components/UserMenuModal.vue';
import logoUrl from '@/assets/images/emn-logo-transparent.svg';

// ============================================
// Mapping des composants de menu par type utilisateur (USER_TYPE)
// ============================================

const NATIVE_MENU_COMPONENTS: Record<string, Component> = {
  [USER_TYPE.STUDENT]: NativeMenuStudent,
  [USER_TYPE.COLLABORATEUR]: NativeMenuEmployee,
  [USER_TYPE.ADMINISTRATOR]: NativeMenuEmployee,
};

const { userType } = useLayout();

// ============================================
// Composant dynamique selon le type utilisateur
// ============================================

/**
 * Sélectionne le composant de menu approprié selon le type utilisateur
 * Fallback sur NativeMenuStudent si le type n'est pas mappé
 */
const nativeMenuComponent = computed<Component>(() => {
  const type = userType.value ?? USER_TYPE.STUDENT;
  return NATIVE_MENU_COMPONENTS[type] ?? NativeMenuStudent;
});

// Navigation menu state
const showMegaMenu = ref(false);
const openMobileMenu = () => {
  showMegaMenu.value = !showMegaMenu.value;
};

// User menu modal
const showUserMenuModal = ref(false);
const toggleUserMenuModal = () => {
  setTimeout(() => {
    showUserMenuModal.value = !showUserMenuModal.value;
  }, 400);
};
</script>

<style scoped>
.tab-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: fixed;
  bottom: 0;
  left: 0;
  padding-bottom: env(safe-area-inset-bottom);
  background-color: transparent;
  z-index: 1000;
}

.tab-bar-container {
  display: flex;
  background: rgba(var(--ion-background-liquid-glass-rgb));
  box-shadow: 1px 0 10px rgba(var(--ion-background-liquid-glass-shadow-rgb));
  backdrop-filter: blur(8.1px);
  -webkit-backdrop-filter: blur(8.1px);
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  border-radius: 10vmin;
  margin-bottom: 20px;
  width: 90%;
  height: 80px;
  padding: 0 20px;
}

.tab-bar-container > button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: white;
}

.nav-icon.logo-icon {
  font-size: 50px;
}

/* Mobile Menu Styles */
.mobile-menu-content {
  --padding-top: 0;
  --padding-bottom: 0;
  --padding-start: 0;
  --padding-end: 0;
  --overflow: visible;
  --scroll-y: true;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
}

/* Ensure menu content is scrollable */
.mobile-menu-content ion-list {
  height: auto;
  min-height: 100%;
}

/* Custom scrollbar styling for webkit browsers */
.mobile-menu-content::-webkit-scrollbar {
  width: 4px;
}

.mobile-menu-content::-webkit-scrollbar-track {
  background: transparent;
}

.mobile-menu-content::-webkit-scrollbar-thumb {
  background-color: var(--ion-color-medium);
  border-radius: 2px;
  opacity: 0.5;
}

.mobile-menu-content::-webkit-scrollbar-thumb:hover {
  background-color: var(--ion-color-primary);
  opacity: 0.8;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 200px;
}

.menu-categories {
  padding: 0;
  padding-bottom: 20px; /* Add bottom padding for better scroll experience */
  overflow-y: auto;
  overflow-x: hidden;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.category-icon {
  font-size: 18px;
  color: var(--ion-color-primary);
}

.menu-link-item {
  --padding-start: 32px;
  --padding-end: 16px;
  --padding-top: 12px;
  --padding-bottom: 12px;
  --border-radius: 0;
  margin: 0 8px;
  border-radius: 8px;
}

.menu-link-item:hover {
  --background: var(--ion-color-light);
}

.section-group {
  margin-top: 16px;
}

.section-divider {
  --padding-start: 24px;
  --padding-top: 8px;
  --padding-bottom: 8px;
  --background: var(--ion-color-step-50);
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--ion-color-medium);
}

.section-link {
  --padding-start: 48px;
}

.link-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.link-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--ion-text-color);
  line-height: 1.2;
}

.link-description {
  font-size: 12px;
  color: var(--ion-color-medium);
  line-height: 1.3;
}

.external-icon {
  opacity: 0.6;
  font-size: 16px;
}

/* Menu header styling */
ion-menu ion-toolbar {
  --background: var(--ion-color-primary);
  --color: white;
}

ion-menu ion-title {
  font-weight: 600;
  font-size: 18px;
}

ion-menu ion-button {
  --color: white;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .menu-link-item {
    --padding-start: 24px;
    --padding-end: 12px;
  }

  .section-link {
    --padding-start: 40px;
  }

  .link-title {
    font-size: 13px;
  }

  .link-description {
    font-size: 11px;
  }
}
</style>
